import { useEffect, useMemo, useState } from "react";

import {
  PosIdentityGatewayClient,
  type CashShiftOpeningRuntime,
  type EmployeeRuntimeActor,
  type ShiftOpeningCarryForward,
} from "../runtime/gateway/identity-client";
import type { TerminalCredentialState } from "../runtime/local-store/terminal-state";
import "./ShiftOpenRuntimeModal.css";

type Props = {
  open: boolean;
  gatewayBaseUrl: string;
  actor: EmployeeRuntimeActor;
  terminal: TerminalCredentialState;
  sessionToken: string;
  online: boolean;
  onClose: () => void;
  onOpened: () => Promise<void> | void;
};

type ViewMode = "LOADING" | "CARRY" | "RECOUNT" | "MANUAL";

function requestKey(prefix: string) {
  const id =
    typeof crypto !== "undefined" && "randomUUID" in crypto
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(16).slice(2)}`;
  return `${prefix}:${id}`;
}

function formatMoney(value: number, currency = "THB") {
  return new Intl.NumberFormat("th-TH", {
    style: "currency",
    currency,
    minimumFractionDigits: 2,
  }).format(value);
}

function formatDateTime(value: string | null) {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString("th-TH", { dateStyle: "medium", timeStyle: "short" });
}

function openingErrorText(value: string) {
  if (value.includes("GATEWAY_UNREACHABLE")) return "ติดต่อ MORE POS Gateway ไม่ได้";
  if (value.includes("GATEWAY_TIMEOUT")) return "MORE POS Gateway ตอบกลับช้าเกินไป";
  if (value.includes("POS_EMPLOYEE_RUNTIME_SESSION_INVALID")) return "เซสชันพนักงานหมดอายุ กรุณาเข้า POS ใหม่";
  if (value.includes("POS_SHIFT_PERMISSION_DENIED")) return "พนักงานคนนี้ไม่มีสิทธิ์เปิดกะ";
  if (value.includes("POS_TERMINAL_ALREADY_HAS_ACTIVE_SHIFT")) return "เครื่องนี้มีกะเปิดอยู่แล้ว";
  if (value.includes("POS_SHIFT_OPENING_CARRY_FORWARD_STALE")) return "เงินตั้งต้นจากกะก่อนมีการเปลี่ยนแปลง กรุณาอ่านข้อมูลใหม่";
  if (value.includes("POS_SHIFT_OPENING_CARRY_FORWARD_NOT_AVAILABLE")) return "เงินตั้งต้นจากกะก่อนถูกใช้แล้วหรือไม่พร้อมใช้งาน";
  if (value.includes("INVALID_EMPLOYEE_CREDENTIAL") || value.includes("POS_SHIFT_EMPLOYEE_NOT_AUTHORIZED")) return "PIN ไม่ถูกต้องหรือพนักงานไม่มีสิทธิ์ในสาขานี้";
  if (value.includes("OPENING_CASH_INVALID")) return "เงินตั้งต้นต้องเป็นจำนวนตั้งแต่ 0 บาทขึ้นไป";
  return value || "เปิดกะไม่สำเร็จ";
}

function positiveCarryLines(carry: ShiftOpeningCarryForward) {
  return carry.lines.filter((line) => Number(line.quantity) > 0);
}

export function ShiftOpenRuntimeModal({
  open,
  gatewayBaseUrl,
  actor,
  terminal,
  sessionToken,
  online,
  onClose,
  onOpened,
}: Props) {
  const [view, setView] = useState<ViewMode>("LOADING");
  const [runtime, setRuntime] = useState<CashShiftOpeningRuntime | null>(null);
  const [manualCash, setManualCash] = useState("");
  const [pin, setPin] = useState("");
  const [quantities, setQuantities] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const gateway = useMemo(
    () => new PosIdentityGatewayClient({ baseUrl: gatewayBaseUrl }),
    [gatewayBaseUrl],
  );

  const carry = runtime?.opening_carry_forward ?? null;

  const recountTotal = useMemo(() => {
    if (!carry) return 0;
    return carry.recount_denominations.reduce((sum, denomination) => {
      const quantity = Number(quantities[denomination.denomination_code] ?? "0");
      if (!Number.isInteger(quantity) || quantity < 0) return sum;
      return sum + quantity * Number(denomination.denomination_value);
    }, 0);
  }, [carry, quantities]);

  async function loadOpeningRuntime() {
    if (!online) {
      setRuntime(null);
      setView("MANUAL");
      setError("การเปิดกะต้อง Online เพื่อให้ Shift Owner ยืนยันข้อมูลล่าสุด");
      return;
    }

    setBusy(true);
    setError(null);
    setView("LOADING");
    const result = await gateway.getCashShiftOpeningRuntime(terminal.deviceSecret, sessionToken);
    setBusy(false);

    if (!result.ok || !result.data) {
      setRuntime(null);
      setView("MANUAL");
      setError(openingErrorText(result.error ?? "POS_SHIFT_OPENING_RUNTIME_LOAD_FAILED"));
      return;
    }

    if (result.data.active_shift) {
      setRuntime(result.data);
      await onOpened();
      onClose();
      return;
    }

    setRuntime(result.data);
    const nextCarry = result.data.opening_carry_forward;
    if (nextCarry) {
      const blankQuantities: Record<string, string> = {};
      for (const denomination of nextCarry.recount_denominations) {
        blankQuantities[denomination.denomination_code] = "0";
      }
      setQuantities(blankQuantities);
      setView("CARRY");
    } else {
      setQuantities({});
      setView("MANUAL");
    }
  }

  useEffect(() => {
    if (!open) return;
    setManualCash("");
    setPin("");
    setRuntime(null);
    setQuantities({});
    setError(null);
    void loadOpeningRuntime();
    // Opening Runtime is intentionally re-read on each explicit Open Shift action.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, gatewayBaseUrl, sessionToken, terminal.deviceSecret]);

  if (!open) return null;

  async function submit(mode: "MANUAL" | "CARRY_FORWARD_CONFIRM" | "RECOUNT") {
    if (!online) {
      setError("การเปิดกะต้อง Online เพื่อให้ Core เป็น Final Authority");
      return;
    }
    if (!pin.trim()) {
      setError("กรุณาใส่ PIN เพื่อยืนยันการเปิดกะ");
      return;
    }

    let openingCash: number | null = null;
    let sourceShiftId: string | null = null;
    let openingLines: Array<{ denomination_code: string; quantity: number }> | null = null;

    if (mode === "MANUAL") {
      const text = manualCash.trim();
      if (!text) {
        setError("กรุณายืนยันเงินสดตั้งต้น หากไม่มีเงินสดให้ใส่ 0");
        return;
      }
      openingCash = Number(text);
      if (!Number.isFinite(openingCash) || openingCash < 0) {
        setError("เงินตั้งต้นต้องเป็นจำนวนตั้งแต่ 0 บาทขึ้นไป");
        return;
      }
    } else {
      if (!carry) {
        setError("ไม่พบเงินตั้งต้นจากกะก่อน กรุณาอ่านข้อมูลใหม่");
        return;
      }
      sourceShiftId = carry.source_shift_id;
      if (mode === "RECOUNT") {
        openingLines = carry.recount_denominations.map((denomination) => {
          const raw = quantities[denomination.denomination_code] ?? "0";
          const quantity = Number(raw);
          return {
            denomination_code: denomination.denomination_code,
            quantity,
          };
        });
        if (openingLines.some((line) => !Number.isInteger(line.quantity) || line.quantity < 0)) {
          setError("จำนวนธนบัตรและเหรียญต้องเป็นจำนวนเต็มตั้งแต่ 0 ขึ้นไป");
          return;
        }
      }
    }

    setBusy(true);
    setError(null);
    const result = await gateway.openCashShiftV2({
      deviceSecret: terminal.deviceSecret,
      sessionToken,
      employeeCode: actor.employee_code,
      pin: pin.trim(),
      openingMode: mode,
      sourceShiftId,
      openingCash,
      openingLines,
      requestKey: requestKey("pos-shift-open-v2"),
      clientContext: {
        runtime: "WEBPOS",
        origin: window.location.origin,
        terminal_node_id: terminal.identity?.node_id ?? null,
        current_actor_employee_id: actor.employee_id,
      },
    });
    setBusy(false);

    if (!result.ok || !result.data?.active_shift) {
      const rawError = result.error ?? "POS_SHIFT_OPEN_FAILED";
      setPin("");
      setError(openingErrorText(rawError));
      if (
        rawError.includes("POS_SHIFT_OPENING_CARRY_FORWARD_STALE") ||
        rawError.includes("POS_SHIFT_OPENING_CARRY_FORWARD_NOT_AVAILABLE") ||
        rawError.includes("POS_TERMINAL_ALREADY_HAS_ACTIVE_SHIFT")
      ) {
        await loadOpeningRuntime();
      }
      return;
    }

    await onOpened();
    onClose();
  }

  return (
    <div className="pos-modal-backdrop shift-opening-backdrop" role="presentation">
      <section className="pos-modal shift-opening-modal" role="dialog" aria-modal="true" aria-labelledby="shift-opening-title">
        <div className="pos-modal__header">
          <div>
            <p className="eyebrow">OPEN POS SHIFT</p>
            <h2 id="shift-opening-title">เปิดกะ</h2>
          </div>
          <button type="button" className="pos-modal__close" disabled={busy} onClick={onClose} aria-label="ปิดหน้าต่างเปิดกะ">×</button>
        </div>

        <div className="shift-opening-actor">
          <span>ผู้รับผิดชอบกะ</span>
          <strong>{actor.employee_name}</strong>
          <small>{actor.employee_code}</small>
        </div>

        {error ? <div className="shift-opening-error">{error}</div> : null}

        {view === "LOADING" ? (
          <div className="shift-opening-loading">
            <strong>กำลังอ่านเงินตั้งต้นล่าสุด…</strong>
            <p>ตรวจจาก Shift Owner เพื่อป้องกันการนำเงินก้อนเดิมไปเปิดกะซ้ำ</p>
          </div>
        ) : null}

        {view === "CARRY" && carry ? (
          <div className="shift-opening-body">
            <div className="shift-opening-carry-card">
              <div>
                <span className="shift-opening-kicker">เงินตั้งต้นจากกะก่อน</span>
                <strong>{formatMoney(Number(carry.suggested_amount), carry.currency_code)}</strong>
                <small>ปิดกะเมื่อ {formatDateTime(carry.source_closed_at)}</small>
              </div>
              <span className="shift-opening-ready-badge">พร้อมส่งต่อ</span>
            </div>

            <p className="shift-opening-note">ใช้เฉพาะเงินที่เลือก “คงไว้กะถัดไป” เท่านั้น เงินรอนำฝากไม่ถูกนำมาเป็นเงินตั้งต้น</p>

            <div className="shift-opening-breakdown" aria-label="รายละเอียดเงินตั้งต้น">
              {positiveCarryLines(carry).length ? positiveCarryLines(carry).map((line) => (
                <div key={line.denomination_code} className="shift-opening-breakdown-row">
                  <span>{line.display_label}</span>
                  <span>× {line.quantity}</span>
                  <strong>{formatMoney(Number(line.amount), carry.currency_code)}</strong>
                </div>
              )) : <p>ยอดนี้ไม่มีรายการธนบัตร/เหรียญที่มากกว่า 0</p>}
            </div>

            <label className="shift-opening-field">
              <span>PIN เพื่อยืนยันการเปิดกะ</span>
              <input required type="password" inputMode="numeric" autoComplete="one-time-code" value={pin} onChange={(event) => setPin(event.target.value)} />
            </label>

            <button className="primary-action shift-opening-primary" type="button" disabled={busy || !online} onClick={() => void submit("CARRY_FORWARD_CONFIRM")}>{busy ? "กำลังเปิดกะ…" : "ยืนยันและเปิดกะ"}</button>
            <button className="secondary-action" type="button" disabled={busy} onClick={() => { setPin(""); setError(null); setView("RECOUNT"); }}>นับใหม่</button>
          </div>
        ) : null}

        {view === "RECOUNT" && carry ? (
          <div className="shift-opening-body">
            <div className="shift-opening-recount-head">
              <div><span className="shift-opening-kicker">นับเงินจริงใหม่</span><strong>{formatMoney(recountTotal, carry.currency_code)}</strong></div>
              <button type="button" disabled={busy} onClick={() => { setPin(""); setError(null); setView("CARRY"); }}>กลับยอดเดิม</button>
            </div>

            <div className="shift-opening-denominations">
              {carry.recount_denominations.map((denomination) => (
                <label key={denomination.denomination_code} className="shift-opening-denomination-row">
                  <span><strong>{denomination.display_label}</strong><small>{denomination.denomination_kind === "NOTE" ? "ธนบัตร" : "เหรียญ"}</small></span>
                  <input type="number" min="0" step="1" inputMode="numeric" value={quantities[denomination.denomination_code] ?? "0"} onChange={(event) => setQuantities((current) => ({ ...current, [denomination.denomination_code]: event.target.value }))} />
                  <strong>{formatMoney(Number(denomination.denomination_value) * Math.max(0, Number(quantities[denomination.denomination_code] ?? "0") || 0), carry.currency_code)}</strong>
                </label>
              ))}
            </div>

            <label className="shift-opening-field">
              <span>PIN เพื่อยืนยันยอดที่นับใหม่</span>
              <input required type="password" inputMode="numeric" autoComplete="one-time-code" value={pin} onChange={(event) => setPin(event.target.value)} />
            </label>

            <button className="primary-action shift-opening-primary" type="button" disabled={busy || !online} onClick={() => void submit("RECOUNT")}>{busy ? "กำลังเปิดกะ…" : `เปิดกะด้วยยอด ${formatMoney(recountTotal, carry.currency_code)}`}</button>
            <p className="shift-opening-note">ถ้ายอดใหม่ต่างจากกะก่อน ระบบเก็บเป็น Opening Variance ของกะใหม่ โดยไม่ย้อนแก้ประวัติกะเดิม</p>
          </div>
        ) : null}

        {view === "MANUAL" ? (
          <div className="shift-opening-body">
            <p className="shift-opening-note">ไม่พบเงินที่ตั้งไว้ส่งต่อจากกะก่อน ให้ยืนยันเงินสดที่มีจริงตอนเริ่มกะ</p>
            <label className="shift-opening-field">
              <span>เงินสดตั้งต้นในลิ้นชัก (ใส่ 0 หากไม่มีเงินสด)</span>
              <input required type="number" min="0" step="0.01" inputMode="decimal" placeholder="เช่น 1000 หรือ 0" value={manualCash} onChange={(event) => setManualCash(event.target.value)} />
            </label>
            <label className="shift-opening-field">
              <span>PIN เพื่อยืนยันการเปิดกะ</span>
              <input required type="password" inputMode="numeric" autoComplete="one-time-code" value={pin} onChange={(event) => setPin(event.target.value)} />
            </label>
            <button className="primary-action shift-opening-primary" type="button" disabled={busy || !online} onClick={() => void submit("MANUAL")}>{busy ? "กำลังเปิดกะ…" : "เปิดกะ"}</button>
            <p className="shift-opening-note">0 บาทเป็นค่าที่ถูกต้อง แต่ช่องว่างจะไม่ถูกตีความเป็น 0 อัตโนมัติ</p>
          </div>
        ) : null}
      </section>
    </div>
  );
}
